import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Group } from "@domain/group/group";
import { useWorkspace, useWorkspaceAssets } from "@domain/group/workspace/hooks";
import { Media } from "@domain/media/media";
import { useIsMobile } from "@utils/hooks";
import { NextButton } from "@ui/components/buttons/nextButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Carousel } from "@ui/components/carousel/carousel";
import { HeaderTitle } from "@ui/components/containers/title";
import { row, Row } from "@ui/components/layout";
import { AssetView } from "@ui/components/media/assetView";
import { ZoomedAsset } from "@ui/components/media/zoomedAsset";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { theme } from "@ui/styles/theme";
import { createToast } from "@ui/toasts/utils";
import { AssetUploadModal } from "@ui/pages/group/workspace/assets/assetUploadModal";
import { Illustrations } from "./workspaceIllustration";
import { WorkspaceLearningContent } from "./workspaceLearningContent";

interface ShareroomWorkspaceProps extends React.HTMLAttributes<HTMLDivElement> {
	workspaceId: string;
	isAdmin: boolean;
	shareroom: Group;
	slugWorkspace: string;
}
export const ShareroomWorkspace: React.FC<ShareroomWorkspaceProps> = ({
	workspaceId,
	isAdmin,
	shareroom,
	slugWorkspace,
	...htmlProps
}) => {
	const { result: workspace, loading } = useWorkspace(workspaceId, slugWorkspace);
	const { result: assets, loading: assetsLoading, totalElements: totalAssets } = useWorkspaceAssets(workspaceId);
	const { workspaceService } = useServices();
	const { createModal } = useModalTools();
	const askConfirmation = useConfirmation(I18n.t("workspace.assets.delete.title"), I18n.t("delete"));
	const [galleryContent, setGalleryContent] = useState<Media | null>(null);
	const isMobile = useIsMobile();
	if (!workspace) {
		return loading ? <ClipLoader /> : null;
	}
	const index = (workspace.id.charCodeAt(0) % 3) + 3;
	const { name } = workspace;
	return (
		<>
			<Link to={Path.Shareroom(shareroom)}>
				<AlignStart color={workspace.color}>
					<GoBackToShareroom>
						<NextButton bgColor={theme.colors.white} inverted />
						<div>
							<HeaderTitle style={{ textAlign: isMobile ? "start" : "center" }}>
								{!isMobile && `${shareroom.name}  >  `}
								<strong>{name}</strong>
							</HeaderTitle>
							<Illustration>{Illustrations[index]}</Illustration>
						</div>
					</GoBackToShareroom>
				</AlignStart>{" "}
			</Link>
			{galleryContent && <ZoomedAsset asset={galleryContent} onClose={() => setGalleryContent(null)} />}
			<Container {...htmlProps}>
				{/*<Primary>{objective}</Primary>*/}
				<div style={{ marginBottom: 0 }}>
					<Carousel
						hideIfEmpty={false}
						title={
							<Row gap={6} align="center">
								<div>{I18n.t("workspace.assets.title", { count: totalAssets ?? 0 })}</div>
								{isAdmin && (
									<RoundedPlusButton
										onClick={() => createModal((close) => <AssetUploadModal workspaceId={workspaceId} close={close} />)}
									/>
								)}
							</Row>
						}
						items={assets}
						itemWidth={164}
						itemHeight={168}
						loading={assetsLoading}
						renderItem={(item) => (
							<ZoomableAssetView
								editable={isAdmin}
								onClick={() => setGalleryContent(item)}
								onDelete={
									isAdmin
										? () =>
												askConfirmation(async () => {
													try {
														await workspaceService.deleteAsset(workspaceId, item.id);
														createToast(I18n.t("success"), I18n.t("workspace.assets.delete.success"), "success");
													} catch {
														createToast(I18n.t("error"), I18n.t("workspace.assets.delete.error"), "error");
													}
												})
										: undefined
								}
								key={item.id}
								asset={item}
							/>
						)}
						rowStyle={{ paddingBottom: 40 }}
					/>
				</div>
				<WorkspaceLearningContent workspaceId={workspaceId} isAdmin={isAdmin} />
			</Container>
		</>
	);
};

const Container = styled.div`
	flex: 1;
	padding: 40px 21px 120px 0;
	position: relative;
	margin-left: 67px;
	margin-right: 67px;
	margin-top: 0;
	max-width: 1140px;
	@media (max-width: 1139px) {
		max-width: 90vw;
	}
	@media (min-width: 1440px) {
		max-width: 1285px;
	}

	@media (max-width: 900px) {
		width: 100vw;
		max-width: 100vw;
		margin-left: 0;
		margin-right: 0;
		padding: 10px 15px 120px;
	}
`;

const ZoomableAssetView = styled(AssetView)`
	cursor: zoom-in;
`;

const AlignStart = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "color",
})<{ color?: string }>`
	align-self: flex-start;
	background-color: ${({ theme, color }) => (!color ? theme.colors.workspaceHeaderColor : color)};
	height: 88px;
	width: 100%;
	margin-top: -57px;
	position: relative;
	overflow: hidden;
	@media (max-width: 900px) {
		margin-top: -64px;
		height: 67px;
	}
`;
const GoBackToShareroom = styled.div`
	${row(12, "flex-start", "center")};
	${({ theme }) => theme.textStyles.text};
	padding-left: 64px;
	padding-top: 22px;

	@media (max-width: 900px) {
		width: 100vw;
		max-width: 100vw;
		margin-left: 0;
		margin-right: 0;
		padding: 10px 15px 120px;
	}
`;

const Illustration = styled.div`
	width: 198px;
	top: 1px;
	right: 33px;
`;
